import { Component } from "react";
import { AlertTriangle, RefreshCw, Copy, Home, LogOut } from "lucide-react";

const APP_VERSION = process.env.REACT_APP_VERSION || "dev";
const API_URL = "/api";

export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, errorId: null, copied: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    const errorId = `err-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    this.setState({ errorInfo, errorId });

    // Send error to backend logging endpoint (fire and forget)
    try {
      fetch(`${API_URL}/errors/log`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          error_id: errorId,
          message: error?.message || String(error),
          stack: error?.stack || "",
          component_stack: errorInfo?.componentStack || "",
          url: window.location.href,
          user_agent: navigator.userAgent,
          app_version: APP_VERSION,
          timestamp: new Date().toISOString()
        })
      }).catch(() => {});
    } catch {
      // Silent fail - don't crash error boundary
    }
  }

  handleCopy = () => {
    const { error, errorInfo, errorId } = this.state;
    const text = `Fehler-ID: ${errorId}
Zeitpunkt: ${new Date().toLocaleString("de-DE")}
URL: ${window.location.href}
Browser: ${navigator.userAgent}
App-Version: ${APP_VERSION}

Fehler: ${error?.message || String(error)}

Stack:
${error?.stack || ""}

Component Stack:
${errorInfo?.componentStack || ""}`;

    navigator.clipboard.writeText(text).then(() => {
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  handleLogout = () => {
    try {
      localStorage.removeItem("token");
      sessionStorage.clear();
    } catch {
      // Silent
    }
    window.location.href = "/login";
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const { error, errorId, copied } = this.state;

    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900 p-4">
        <div className="max-w-2xl w-full bg-white dark:bg-slate-800 rounded-lg shadow-xl border border-slate-200 dark:border-slate-700 p-6 sm:p-8">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-1">
                Etwas ist schiefgelaufen
              </h1>
              <p className="text-slate-600 dark:text-slate-400">
                Die Anwendung hat einen unerwarteten Fehler. Bitte versuchen Sie es erneut oder kontaktieren Sie den Administrator.
              </p>
            </div>
          </div>

          <div className="bg-slate-50 dark:bg-slate-900/50 rounded-md p-4 mb-6 border border-slate-200 dark:border-slate-700">
            <div className="text-sm font-mono text-slate-700 dark:text-slate-300 break-words">
              <div className="mb-2">
                <span className="text-slate-500 dark:text-slate-400">Fehler-ID:</span>{" "}
                <span className="font-semibold" data-testid="error-id">{errorId}</span>
              </div>
              <div>
                <span className="text-slate-500 dark:text-slate-400">Nachricht:</span>{" "}
                <span className="text-red-600 dark:text-red-400">{error?.message || String(error)}</span>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={this.handleReload}
              className="inline-flex items-center gap-2 px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-md font-medium transition-colors"
              data-testid="error-reload-btn"
            >
              <RefreshCw className="w-4 h-4" />
              Seite neu laden
            </button>
            <button
              type="button"
              onClick={this.handleGoHome}
              className="inline-flex items-center gap-2 px-4 py-2 bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-900 dark:text-slate-100 rounded-md font-medium transition-colors"
              data-testid="error-home-btn"
            >
              <Home className="w-4 h-4" />
              Zur Startseite
            </button>
            <button
              type="button"
              onClick={this.handleLogout}
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-red-700 dark:text-red-300 rounded-md font-medium transition-colors"
              data-testid="error-logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Abmelden
            </button>
            <button
              type="button"
              onClick={this.handleCopy}
              className="inline-flex items-center gap-2 px-4 py-2 bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-900 dark:text-slate-100 rounded-md font-medium transition-colors"
              data-testid="error-copy-btn"
            >
              <Copy className="w-4 h-4" />
              {copied ? "Kopiert!" : "Fehler kopieren"}
            </button>
          </div>

          <details className="mt-6 text-sm">
            <summary className="cursor-pointer text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200">
              Technische Details anzeigen
            </summary>
            <pre className="mt-3 p-3 bg-slate-900 text-slate-100 rounded-md overflow-x-auto text-xs">
              {error?.stack || "Kein Stack-Trace verfügbar"}
            </pre>
          </details>

          <p className="text-xs text-slate-500 dark:text-slate-400 mt-6 text-center">
            Browser: {navigator.userAgent.slice(0, 80)}...
          </p>
        </div>
      </div>
    );
  }
}

// Errors to ignore - these are common browser noise that don't indicate real bugs
const IGNORED_ERROR_PATTERNS = [
  /ResizeObserver loop/i,
  /ResizeObserver loop limit exceeded/i,
  /Non-Error promise rejection captured/i,
  /Script error\.?$/i
];

function shouldIgnoreError(message) {
  if (!message) return false;
  return IGNORED_ERROR_PATTERNS.some((p) => p.test(message));
}

// Global error handlers - catch unhandled errors outside React tree
let errorHandlersInstalled = false;
export function installGlobalErrorHandlers() {
  if (errorHandlersInstalled) return;
  errorHandlersInstalled = true;

  const sendError = (data) => {
    if (shouldIgnoreError(data?.message)) return;
    try {
      fetch(`${API_URL}/errors/log`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          error_id: `err-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
          ...data,
          url: window.location.href,
          user_agent: navigator.userAgent,
          app_version: APP_VERSION,
          timestamp: new Date().toISOString()
        })
      }).catch(() => {});
    } catch {
      // Silent
    }
  };

  window.addEventListener("error", (event) => {
    sendError({
      message: event.message,
      stack: event.error?.stack || "",
      source: event.filename,
      line: event.lineno,
      column: event.colno,
      type: "window.error"
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    sendError({
      message: event.reason?.message || String(event.reason),
      stack: event.reason?.stack || "",
      type: "unhandled_promise_rejection"
    });
  });
}
